import { useCallback, useEffect, useMemo } from 'react';
import {
	createAndSendShieldHeartBeatEvent,
	getIsShieldEnabled,
	LAST_SHIELD_HEARTBEAT_EVENT_TIMESTAMP_STORAGE_KEY,
	ONE_DAY_IN_MILLISECONDS,
	shouldSendHeartbeat,
} from 'App/ShieldPRD/util';
import { useConfigurationContext } from 'Configuration/useConfigurationContext';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { isNativeClient } from 'Environment/launchResource/device';
import { setInLocalStorage } from 'javascript/sf/Storage';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import { useCasTelemetryAdapter } from 'Workspace/CAS/useCasTelemetryAdapter';
import { useLoadableResourceContext } from 'Workspace/ResourceProvider';
import { useUserContext } from 'Workspace/UserContext';

interface UseShieldPRDTelemetryProps<TContext = any> {
	featureFlag: FeatureFlag;
	context: TContext;
	getExtraPayload?: (ctx: TContext) => Promise<Record<string, any>> | Record<string, any>;
}

export const useShieldPRDTelemetry = ({
	featureFlag,
	context,
	getExtraPayload,
}: UseShieldPRDTelemetryProps) => {
	const resourceContext = useLoadableResourceContext();
	const userContext = useUserContext();
	const { workspaceConfiguration } = useConfigurationContext();
	const CASEventPublisher = useCasTelemetryAdapter();
	const { userDetails, hasLoggedIn } = userContext;
	const isShieldEnabledFeature = useFeatureCanary(featureFlag);

	const isUserReadyForTelemetry = useCallback(async () => {
		const isLoggedIn = await hasLoggedIn();
		return isLoggedIn && userDetails?.userDisplayName !== null;
	}, [hasLoggedIn, userDetails?.userDisplayName]);

	const shouldDisableShieldPrd = useMemo(() => {
		return (
			isNativeClient() ||
			!isShieldEnabledFeature ||
			resourceContext.loading ||
			userContext.loading ||
			IS_ON_PREM
		);
	}, [isShieldEnabledFeature, resourceContext.loading, userContext.loading]);

	useEffect(() => {
		if (shouldDisableShieldPrd) {
			return undefined;
		}

		const checkAndSendTelemetry = async () => {
			if (!shouldSendHeartbeat() || !(await isUserReadyForTelemetry())) {
				return;
			}

			const isShieldEnabled = getIsShieldEnabled(
				workspaceConfiguration,
				context?.leasingProperties
			);

			const extraPayload =
				typeof getExtraPayload === 'function' ? await getExtraPayload(context) : {};

			const eventPayload = {
				CASEventPublisher,
				resources: resourceContext?.value?.resources,
				userDisplayName: userDetails?.userDisplayName,
				storeGuid: workspaceConfiguration.storeIdentifiers?.storeGuid,
				userId: userDetails?.userId,
				isShieldEnabled,
				...extraPayload,
			};

			await createAndSendShieldHeartBeatEvent(eventPayload);

			setInLocalStorage(LAST_SHIELD_HEARTBEAT_EVENT_TIMESTAMP_STORAGE_KEY, Date.now());
		};

		checkAndSendTelemetry();
		const intervalId = setInterval(checkAndSendTelemetry, ONE_DAY_IN_MILLISECONDS);
		return () => clearInterval(intervalId);
	}, [
		userDetails?.userDisplayName,
		resourceContext?.value?.resources,
		userDetails?.userId,
		CASEventPublisher,
		workspaceConfiguration,
		isUserReadyForTelemetry,
		getExtraPayload,
		shouldDisableShieldPrd,
		context,
	]);
};
